import BookCallCTA from './BookCallCTA';
import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Loader2 } from 'lucide-react';

import { submitCallback } from '../lib/callback';
import { trackEvent } from '../lib/analytics';

export default function FinalCTA() {
  const [formData, setFormData] = useState({ name: '', phone: '', leads: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');

  const sending = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending.current) return;
    sending.current = true;
    setStatus('submitting');
    trackEvent('callback_submit', { source: 'final_cta' });
    try {
      await submitCallback({ ...formData, source: 'final_cta' });
      setStatus('done');
      trackEvent('callback_success', { source: 'final_cta' });
    } catch {
      setStatus('error');
      trackEvent('callback_error', { source: 'final_cta' });
    } finally {
      sending.current = false;
    }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(212,161,13,0.12)_0%,transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(212,161,13,0.06)_0%,transparent_60%)]" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <p className="text-[#d4a10d] font-semibold text-sm uppercase tracking-widest mb-4">
            Limited spots per month
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Your AI ISA.<br className="hidden sm:block" /> Built for your business.
          </h2>
          <p className="text-xl text-gray-300 max-w-xl mx-auto">
            Book a call to scope your system, setup fee, and ongoing retainer. Builds typically take 2 to 3 weeks.
          </p>
          <BookCallCTA className="mt-6" />
          <p className="text-sm text-gray-400 mt-5">Prefer a callback? Leave your details below.</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          viewport={{ once: true }}
          className="max-w-lg mx-auto"
        >
          {status === 'done' ? (
            <div role="status" className="bg-white/10 border border-[#d4a10d]/40 rounded-2xl p-10 text-center">
              <CheckCircle className="w-14 h-14 text-[#d4a10d] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Got it, we'll be in touch soon.</h3>
              <p className="text-gray-300">Expect a call within one business day to walk you through everything.</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-white/5 border border-white/10 rounded-2xl p-8 space-y-4 backdrop-blur-sm"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="callback-name" className="block text-sm font-medium text-gray-300 mb-1.5">First name *</label>
                  <input
                    id="callback-name"
                    autoComplete="given-name"
                    maxLength={80}
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Jane"
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#d4a10d]/60 focus:ring-1 focus:ring-[#d4a10d]/40 transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="callback-phone" className="block text-sm font-medium text-gray-300 mb-1.5">Phone number *</label>
                  <input
                    id="callback-phone"
                    autoComplete="tel"
                    maxLength={30}
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                    placeholder="+1 XXX XXX XXXX"
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#d4a10d]/60 focus:ring-1 focus:ring-[#d4a10d]/40 transition-all"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="callback-leads" className="block text-sm font-medium text-gray-300 mb-1.5">
                  How many leads do you get per month?
                </label>
                <select
                  id="callback-leads"
                  value={formData.leads}
                  onChange={e => setFormData(p => ({ ...p, leads: e.target.value }))}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#d4a10d]/60 focus:ring-1 focus:ring-[#d4a10d]/40 transition-all"
                >
                  <option value="" className="bg-gray-900">Select your volume...</option>
                  <option value="20-50" className="bg-gray-900">20–50 leads / month</option>
                  <option value="50-100" className="bg-gray-900">50–100 leads / month</option>
                  <option value="100-250" className="bg-gray-900">100–250 leads / month</option>
                  <option value="250+" className="bg-gray-900">250+ leads / month</option>
                </select>
              </div>

              {status === 'error' && (
                <p role="alert" className="text-red-300 text-sm">We couldn’t confirm your request. Check your phone number and try again, or <a className="underline" href="tel:7096498492">call (709) 649-8492</a>.</p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full flex items-center justify-center gap-2 bg-[#d4a10d] hover:bg-[#b8940b] disabled:opacity-60 text-white font-bold py-4 rounded-lg text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                {status === 'submitting' ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Sending...</>
                ) : (
                  <>Get My Callback <ArrowRight className="w-5 h-5" /></>
                )}
              </button>

              <p className="text-center text-xs text-gray-500">
                No spam. We'll call once to set up your strategy session.
              </p>
            </form>
          )}
        </motion.div>

        {/* Trust signals */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="flex flex-wrap items-center justify-center gap-6 mt-10 text-sm text-gray-400"
        >
          {["Client-hosted on your accounts", "Typical build: 2 to 3 weeks", "You own the system", "CASL + TCPA compliant"].map(t => (
            <span key={t} className="flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4 text-[#d4a10d] flex-shrink-0" />
              {t}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
